import { useReducedMotion as useReducedMotionFM, type Transition, type Variants } from "framer-motion";

/**
 * Centralized motion-preference hook.
 *
 * All animated components in the app should call this instead of importing
 * `useReducedMotion` from framer-motion directly. It returns a boolean and a
 * set of pre-resolved spring/transition presets that automatically degrade to
 * an instant transition when the user has requested reduced motion.
 *
 * Tip: pair the returned `t.spring` / `t.snap` with framer-motion's
 * `transition` prop, and use the helper variant builders for consistent
 * page/row/drawer transitions across the app.
 */
export function useMotion() {
  const reduce = useReducedMotionFM() ?? false;

  const spring: Transition = reduce
    ? { duration: 0 }
    : { type: "spring", stiffness: 280, damping: 28 };

  const snap: Transition = reduce
    ? { duration: 0 }
    : { type: "spring", stiffness: 320, damping: 30, mass: 0.8 };

  const fade: Transition = reduce
    ? { duration: 0 }
    : { duration: 0.18, ease: [0.32, 0.72, 0, 1] };

  // Page-level enter/exit for route transitions.
  const pageVariants: Variants = reduce
    ? {
        initial: { opacity: 1 },
        animate: { opacity: 1 },
        exit: { opacity: 1 },
      }
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -6 },
      };

  // Row-level stagger for tables / lists.
  const rowVariants: Variants = reduce
    ? {
        initial: { opacity: 1 },
        animate: { opacity: 1 },
      }
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
      };

  // Drawer / sheet slide.
  const drawerVariants: Variants = reduce
    ? { open: { x: 0 }, closed: { x: 0 } }
    : {
        open: { x: 0, transition: { type: "spring", stiffness: 280, damping: 30 } },
        closed: { x: "-100%", transition: { type: "spring", stiffness: 280, damping: 32 } },
      };

  return {
    reduce,
    t: { spring, snap, fade },
    variants: {
      page: pageVariants,
      row: rowVariants,
      drawer: drawerVariants,
    },
  };
}

/**
 * Backwards-compatible re-export — returns just the boolean. Prefer `useMotion()`
 * for new code so transitions stay on a shared spec.
 */
export function usePrefersReducedMotion(): boolean {
  return useReducedMotionFM() ?? false;
}
